import { useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';

import { Sidebar, Text, type TypeSidebarNotification } from '@thefarmersfront/kitchen';

import { SmartSearchPanelGroup } from '~/components/common';
import { Header } from '~/components/layout';

import { generateSidebarConfig, getAccessibleRoutes } from '~/configs';
import { PATH } from '~/constants';
import type { TypeLogisticsType, TypeOwnerType } from '~/constants/common';
import { useGetReturnDisposalUnprocessedCountQuery } from '~/hooks/receive';

import styles from './Layout.module.scss';

type TypeLayoutProps = {
  ownerType: TypeOwnerType;
  logisticsTypes: TypeLogisticsType[];
};

const Layout = ({ ownerType, logisticsTypes }: TypeLayoutProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // 회송/폐기 관리 접근 권한이 있을 때만 GNB 미처리 건수를 조회한다.
  const hasReturnDisposalAccess = useMemo(
    () => getAccessibleRoutes(ownerType, logisticsTypes).some((route) => route.path === PATH.RECEIVE_RETURN_DISPOSAL),
    [ownerType, logisticsTypes]
  );

  const { data: unprocessedCount } = useGetReturnDisposalUnprocessedCountQuery({ enabled: hasReturnDisposalAccess });

  // 미처리 건수가 있을 때만 서브메뉴에 숫자 뱃지, 상위 '입고 관리' 그룹에 dot을 주입한다.
  const notifications = useMemo<Record<string, TypeSidebarNotification> | undefined>(
    () =>
      unprocessedCount
        ? {
            [PATH.RECEIVE_RETURN_DISPOSAL]: { count: unprocessedCount },
            [PATH.RECEIVE]: { dot: true },
          }
        : undefined,
    [unprocessedCount]
  );

  const menus = useMemo(
    () => generateSidebarConfig(ownerType, logisticsTypes, notifications),
    [ownerType, logisticsTypes, notifications]
  );

  const handleClickSidebarItem = (path: string) => {
    navigate(path);
  };

  return (
    <div className={styles['layout']}>
      <div className={styles['header-container']}>
        <Header />
      </div>
      <SmartSearchPanelGroup>
        <div className={styles['main-container']}>
          <Sidebar
            currentKey={pathname}
            menus={menus}
            onSelectItem={handleClickSidebarItem}
            bottomElement={
              <div className={styles['footer-container']}>
                <Text color="secondary" size="xsmall">
                  &#169; Kurly Corp.
                  <br />
                  All rights reserved.
                </Text>
              </div>
            }
          />
          <div className={styles['content-container']}>
            <div className={styles['page-wrapper']}>
              <Outlet />
            </div>
          </div>
        </div>
      </SmartSearchPanelGroup>
    </div>
  );
};

export default Layout;
